import { ROUTE_QUESTIONNAIRE } from '@/constants/routes';
import {
  Box,
  BoxProps,
  IconArrowRight,
  Link,
  LinkSize,
  ProgressBar,
  Text,
  TextProps,
  useBreakpointValue,
} from '@cmpsr/components';
import { Link as ReactRouterLink } from 'react-router-dom';

export type IncompleteAssessmentVariant = 'mobileNavBar' | 'large' | 'small';

type IncompleteAssessmentProps = {
  assessmentProgress?: number;
  variant: IncompleteAssessmentVariant;
};

function useLinkSize(variant: IncompleteAssessmentVariant): LinkSize {
  const locationSize: LinkSize = useBreakpointValue({ base: 'm', xl: 'l' }, { ssr: false }) ?? 'm';
  const linkSize: LinkSize = variant === 'mobileNavBar' ? 's' : locationSize;
  return linkSize;
}

function getContainerProps(variant: IncompleteAssessmentVariant): BoxProps {
  switch (variant) {
    case 'mobileNavBar':
      return {
        px: 'spacer-5',
        py: 'spacer-4',
        width: '100%',
        textAlign: 'left',
      };
    case 'small':
      return {
        px: { base: 'spacer-6', xl: 'spacer-8' },
        py: { base: 'spacer-6', xl: 'spacer-9' },
        width: { base: '100%', lg: '320px', xl: '360px', xxl: '400px' },
        textAlign: 'left',
      };
    case 'large':
      return {
        px: {
          base: 'spacer-6',
          lg: 'spacer-40',
          xl: 'spacer-64',
          xxl: 'spacer-80',
        },
        py: { base: 'spacer-6', lg: 'spacer-9' },
        width: '100%',
        textAlign: { base: 'left', md: 'center' },
      };
  }
}

function getHeadlineProps(variant: IncompleteAssessmentVariant): TextProps {
  switch (variant) {
    case 'mobileNavBar':
      return {
        variant: 'text-body-floating-label-medium',
        mb: 'spacer-half',
      };
    case 'small':
    case 'large':
      return {
        variant: 'text-header-XS',
        mb: 'spacer-2',
      };
  }
}

function getAssessmentProps(variant: IncompleteAssessmentVariant): TextProps {
  switch (variant) {
    case 'mobileNavBar':
      return {
        variant: 'text-header-L',
        mb: 'spacer-3',
      };
    case 'small':
    case 'large':
      return {
        variant: 'text-header-XL',
        mb: 'spacer-4',
      };
  }
}

export default function IncompleteAssessment({ assessmentProgress, variant }: IncompleteAssessmentProps) {
  const linkSize = useLinkSize(variant);

  return (
    <Box {...getContainerProps(variant)} borderRadius="radii-4" backgroundColor="alert-error-default">
      <Text {...getHeadlineProps(variant)} textTransform="uppercase">
        incomplete assessment
      </Text>
      <Text {...getAssessmentProps(variant)}>{`${assessmentProgress}% done`}</Text>
      <ProgressBar
        size="xs"
        mb={variant === 'small' ? { base: 'spacer-4', xl: 'spacer-5' } : { base: 'spacer-4', lg: 'spacer-5' }}
        min={0}
        max={100}
        value={assessmentProgress}
      />
      {variant !== 'mobileNavBar' && (
        <Text
          variant="text-body-regular"
          mb={variant === 'small' ? { base: 'spacer-5', lg: 'spacer-6' } : { base: 'spacer-5', xl: 'spacer-6' }}
        >
          You haven't completed the whole assessment. Please make sure you answer all questions to receive an accurate
          risk assessment.
        </Text>
      )}
      <Link
        as={ReactRouterLink}
        to={ROUTE_QUESTIONNAIRE}
        variant="primary"
        size={linkSize}
        trailingIcon={<IconArrowRight />}
        width={variant === 'mobileNavBar' ? '100%' : 'auto'}
        justifyContent="center"
      >
        Complete Assessment
      </Link>
    </Box>
  );
}
